import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { LoaderButton } from "@/components/ui/loader-button";
import { ContactForm } from "@/components/program/ContactForm";
import { LogoUpload } from "@/components/program/LogoUpload";
import countries from "@/constants/array/country_city.json";
import { PAYMENT_CURRENCY_OPTIONS } from "@/constants/program.constant";
import { APIClient, ClientApi } from "@/api";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import VirtualizedCountrySelect from "@/components/ui/virtualized-country-select";
import { useToast } from "@/hooks/use-toast";
import { BASE_URL } from "@/config";
import { getFileUrl } from "@/utils/common.utils";

const EditTransferPartnerSchema = Yup.object().shape({
  id: Yup.string().optional().nullable(),
  logo: Yup.mixed().optional().nullable(),
  partnerName: Yup.string()
    .required("Partner Name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Partner name cannot contain special characters"
    ),
  partnerCode: Yup.string(),
  partnerDescription: Yup.string().required("Partner Description is required"),
  address: Yup.string().required("Address is required"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),
  loyaltyCurrency: Yup.string()
    .required("Loyalty Currency is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Loyalty currency cannot contain special characters"
    ),
  redemptionCPP: Yup.string()
    .required("Redemption CPP is required")
    .matches(/^\d+(\.\d+)?$/, "Redemption CPP must be a positive number")
    .test(
      "max-value",
      "Redemption CPP cannot be greater than 100",
      function (value) {
        if (!value) return true;
        const numValue = parseFloat(value);
        return numValue <= 100;
      }
    ),
  clientId: Yup.string().required("Client is required"),
  paymentCurrency: Yup.string().required("Payment Currency is required"),
  pointsIncrement: Yup.string()
    .required("Points Increment is required")
    .matches(
      /^[1-9]\d*(\.\d+)?$/,
      "Points increment must be a positive number greater than 0"
    ),
  vatNo: Yup.string(),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid HTTP URL format"),
  contacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required("Contact name is required")
        .matches(
          /^[A-Za-z\s]+$/,
          "Full name can only contain letters and spaces"
        ),
      email: Yup.string()
        .required("Email is required")
        .email("Email must be a valid email address"),
      role: Yup.string()
        .required("Role is required")
        .matches(/^[A-Za-z\s]+$/, "Role can only contain letters and spaces"),
      countryCode: Yup.string(),
      phoneNumber: Yup.string()
        .required("Required")
        .matches(/^\d+$/, "Only numbers allowed")
        .min(7, "Min 7 digits")
        .max(15, "Max 15 digits"),
    })
  ),
});

export function EditTransferPartnerSheet({
  open,
  onOpenChange,
  transferPartner,
  contacts,
  onSuccess,
}) {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  console.log(contacts, "contacts");

  // Map contacts prop to form structure if provided
  const mappedContacts =
    contacts && contacts.length > 0
      ? contacts.map((c) => ({
          name: c.contactName,
          email: c.contactEmail,
          role: c.contactRole,
          countryCode: c.phoneCountryCode || "",
          phoneNumber: c.phoneNumber || "",
          id: c.id,
        }))
      : transferPartner?.contacts?.length
      ? transferPartner.contacts.map((c) => ({
          name: c.contactName,
          email: c.contactEmail,
          role: c.contactRole,
          countryCode: c.phoneCountryCode || "",
          phoneNumber: c.phoneNumber || "",
          id: c.id,
        }))
      : [
          {
            name: "",
            email: "",
            countryCode: "+971",
            phoneNumber: "",
            role: "",
            id: null,
          },
        ];

  const form = useForm({
    resolver: yupResolver(EditTransferPartnerSchema),
    defaultValues: {
      id: transferPartner?.id || null,
      logo: null, // will be set by LogoUpload
      partnerName: transferPartner?.partnerName || "",
      partnerCode: transferPartner?.partnerCode || "",
      partnerDescription: transferPartner?.businessDescription || "",
      address: transferPartner?.businessLocation || "",
      country: transferPartner?.country || "",
      city: transferPartner?.city || "",
      loyaltyCurrency: transferPartner?.loyaltyCurrency || "",
      redemptionCPP:
        transferPartner?.redemptionCpp || transferPartner?.costPerPoint || "",
      clientId: transferPartner?.clientVO?.id
        ? String(transferPartner.clientVO.id)
        : "",
      paymentCurrency: transferPartner?.currencyVO?.iso3CurrencyCode || "USD",
      pointsIncrement: transferPartner?.pointsIncrement || "",
      vatNo: transferPartner?.taxId || "",
      websiteUrl: transferPartner?.websiteUrl || "",
      contacts: mappedContacts,
    },
  });

  useEffect(() => {
    getClients();
    // Set selected country for city select
    if (transferPartner?.country) {
      const found = (countries as any)?.find(
        (c) => c.name === transferPartner.country
      );
      setSelectedCountry(found || null);
    }
  }, [transferPartner]);

  useEffect(() => {
    form.reset({
      id: transferPartner?.id || null,
      logo: null,
      partnerName: transferPartner?.partnerName || "",
      partnerCode: transferPartner?.partnerCode || "",
      partnerDescription: transferPartner?.businessDescription || "",
      address: transferPartner?.businessLocation || "",
      country: transferPartner?.country || "",
      city: transferPartner?.city || "",
      loyaltyCurrency: transferPartner?.loyaltyCurrency || "",
      redemptionCPP:
        transferPartner?.redemptionCpp || transferPartner?.costPerPoint || "",
      clientId: transferPartner?.clientVO?.id
        ? String(transferPartner.clientVO.id)
        : "",
      paymentCurrency: transferPartner?.currencyVO?.iso3CurrencyCode || "USD",
      pointsIncrement: transferPartner?.pointsIncrement || "",
      vatNo: transferPartner?.taxId || "",
      websiteUrl: transferPartner?.websiteUrl || "",
      contacts: mappedContacts,
    });
  }, [transferPartner, contacts]);

  const getClients = async () => {
    try {
      const res = await ClientApi.getAllClients();
      setClients(
        (res?.data || [])?.map((client) => ({
          value: `${client?.id || ""}`,
          label: client?.clientName,
        }))
      );
    } catch (error) {
      // handle error
    }
  };

  // Check which tabs have validation errors
  const tabsWithErrors = useMemo(() => {
    const errors = form.formState.errors;
    const tabsWithErrors = [];

    // Check General Info tab fields
    if (
      errors.partnerName ||
      errors.partnerCode ||
      errors.partnerDescription ||
      errors.address ||
      errors.websiteUrl ||
      errors.clientId
    ) {
      tabsWithErrors.push("general");
    }

    // Check Loyalty & Location Info tab fields
    if (
      errors.redemptionCPP ||
      errors.paymentCurrency ||
      errors.pointsIncrement ||
      errors.country ||
      errors.city
    ) {
      tabsWithErrors.push("loyalty_location");
    }

    // Check Contact Info tab fields
    if (errors.contacts) {
      tabsWithErrors.push("contacts");
    }

    return tabsWithErrors;
  }, [form.formState.errors]);

  const onSubmit = async (formData) => {
    // Trigger validation for all fields
    const isValid = await form.trigger();

    if (!isValid) {
      // Check for validation errors and switch to first tab with errors
      if (tabsWithErrors.length > 0) {
        setActiveTab(tabsWithErrors[0]);
        toast({
          title: "Validation Error",
          description: "Please fix the validation errors before submitting",
          variant: "destructive",
        });
        return; // Don't submit if there are validation errors
      }
    }

    setIsSubmitting(true);
    try {
      const { contacts, ...partnerData } = formData;
      const formDataToSend = new FormData();
      if (formData.logo) {
        formDataToSend.append("transferPartnerLogo", formData.logo);
      }
      const currency = PAYMENT_CURRENCY_OPTIONS?.find(
        (currency) => currency?.value === formData?.paymentCurrency
      );
      const transferPartnerData = {
        id: transferPartner?.id, // always send the id for update
        partnerName: formData?.partnerName,
        partnerCode: formData?.partnerCode,
        businessDescription: formData?.partnerDescription,
        businessLocation: formData?.address,
        country: selectedCountry?.name || formData?.country,
        city: formData?.city,
        loyaltyCurrency: formData?.loyaltyCurrency,
        costPerPoint: formData?.redemptionCPP,
        clientId: formData?.clientId,
        currencyId: currency?.id,
        pointsIncrement: formData?.pointsIncrement,
        taxId: formData?.vatNo,
        websiteUrl: formData?.websiteUrl,
        createdDateInMillis: transferPartner?.createdDateInMillis || Date.now(),
        lastUpdatedDateInMillis: Date.now(),
      };
      const blob = new Blob([JSON.stringify(transferPartnerData)], {
        type: "application/json",
      });
      formDataToSend.append("transferPartnerVO", blob);
      // Update transfer partner
      const res = await APIClient(
        "/transferPartner/createOrUpdateTransferPartner",
        formDataToSend,
        { requestType: "post", contentType: "multipart/form-data" }
      );
      const apiData = res?.data;
      // Update contacts
      if (transferPartner?.id && contacts && contacts.length > 0) {
        const contactPromises = contacts.map(async (contact) => {
          const contactData = {
            contactName: contact.name,
            contactEmail: contact.email,
            contactRole: contact.role,
            phoneNumber: contact.phoneNumber,
            phoneCountryCode: contact.countryCode,
            partnerId: transferPartner.id,
            id: contact?.id,
          };
          return APIClient(
            "/transferPartner/createOrUpdateTransferPartnerContact",
            contactData,
            { requestType: "post" }
          );
        });
        await Promise.all(contactPromises);
      }

      const updatedPartner = {
        ...transferPartner,
        partnerName: formData?.partnerName,
        partnerCode: formData?.partnerCode,
        businessDescription: formData?.partnerDescription,
        businessLocation: formData?.address,
        country: selectedCountry?.name || formData?.country,
        city: formData?.city,
        loyaltyCurrency: formData?.loyaltyCurrency,
        redemptionCpp: formData?.redemptionCPP,
        costPerPoint:
          Number(formData?.redemptionCPP) || transferPartner?.costPerPoint,
        taxId: formData?.vatNo,
        websiteUrl: formData?.websiteUrl,
        pointsIncrement: formData?.pointsIncrement,
        lastUpdatedDateInMillis: Date.now(),
        ...(currency && {
          currencyVO: {
            ...transferPartner?.currencyVO,
            id: currency.id,
            iso3CurrencyCode: currency.value,
            currencyName: currency.label,
          },
        }),
        ...(apiData?.partnerLogoUri && {
          partnerLogoUri: apiData.partnerLogoUri,
        }),
      };

      onOpenChange(false);
      onSuccess?.(updatedPartner);
      toast({
        title: "Success",
        description: "Program updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update program",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Program</SheetTitle>
        </SheetHeader>
        <div className="py-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="mb-6 w-full">
                  <TabsTrigger
                    value="general"
                    className={`flex-1 ${
                      tabsWithErrors.includes("general")
                        ? "border-red-500 text-red-600 bg-red-50"
                        : ""
                    }`}
                  >
                    General Info
                    {tabsWithErrors.includes("general") && (
                      <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="loyalty_location"
                    className={`flex-1 ${
                      tabsWithErrors.includes("loyalty_location")
                        ? "border-red-500 text-red-600 bg-red-50"
                        : ""
                    }`}
                  >
                    Loyalty & Location Info
                    {tabsWithErrors.includes("loyalty_location") && (
                      <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="contacts"
                    className={`flex-1 ${
                      tabsWithErrors.includes("contacts")
                        ? "border-red-500 text-red-600 bg-red-50"
                        : ""
                    }`}
                  >
                    Contact Info
                    {tabsWithErrors.includes("contacts") && (
                      <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="general" className="space-y-6">
                  <div className="space-y-2">
                    <FormLabel>Logo</FormLabel>
                    <div className="flex items-center gap-4">
                      <div
                        className="h-16 w-16 rounded-md border bg-white flex items-center justify-center overflow-hidden cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <img
                          src={(() => {
                            const logo = form.watch("logo");
                            if (logo && logo instanceof File) {
                              return URL.createObjectURL(logo);
                            }
                            if (transferPartner?.partnerLogoUri) {
                              return getFileUrl(transferPartner.partnerLogoUri);
                            }
                            return "/placeholder.svg";
                          })()}
                          alt={
                            form.watch("partnerName") ||
                            transferPartner?.partnerName ||
                            "Partner Logo"
                          }
                          className="object-cover h-full w-full rounded-md"
                        />
                      </div>
                      <div>
                        <FormField
                          control={form.control}
                          name="logo"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="file"
                                  accept="image/*"
                                  className="text-sm hidden"
                                  ref={fileInputRef}
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      field.onChange(e.target.files[0]);
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                              <p className="text-xs text-gray-500 mt-1">
                                Recommended: 400x400px, max 2MB
                              </p>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <FormField
                    control={form.control}
                    name="partnerName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Emirates Skywards"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^a-zA-Z0-9\s]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          The name of your transfer partner
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="partnerCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. EMIR-001"
                            {...field}
                            disabled={true}
                            className="bg-gray-100"
                          />
                        </FormControl>
                        <FormDescription>
                          A unique identifier for the partner
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="partnerDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description*</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe the transfer partner..."
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Brief description of the transfer partner
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={true}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Client" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {clients?.map((client) => (
                              <SelectItem
                                key={client?.value}
                                value={client?.value}
                              >
                                {client?.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vatNo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. AE123456789" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="websiteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://www.example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          The official website of the transfer partner
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>
                <TabsContent value="loyalty_location" className="space-y-6">
                  <FormField
                    control={form.control}
                    name="loyaltyCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Loyalty Currency*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Skywards Miles"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^a-zA-Z0-9\s]/g,
                                ""
                              );
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          The name of the loyalty currency
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="redemptionCPP"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Redemption CPP*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 0.012"
                            {...field}
                            onChange={(e) => {
                              let value = e.target.value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              // Prevent multiple decimal points
                              const parts = value.split(".");
                              if (parts.length > 2) {
                                value =
                                  parts[0] + "." + parts.slice(1).join("");
                              }
                              // Prevent values greater than 100
                              const numValue = parseFloat(value);
                              if (numValue > 100) {
                                value = "100";
                              }
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              // Prevent negative sign
                              if (e.key === "-") {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent number input from changing value on scroll
                              e.currentTarget.blur();
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Cost per point for redemption
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="paymentCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Currency*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_CURRENCY_OPTIONS?.map((currency) => (
                              <SelectItem
                                key={currency?.id}
                                value={currency?.value}
                              >
                                {currency?.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pointsIncrement"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Points Increment*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 100"
                            {...field}
                            onChange={(e) => {
                              let value = e.target.value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              // Prevent multiple decimal points
                              const parts = value.split(".");
                              if (parts.length > 2) {
                                value =
                                  parts[0] + "." + parts.slice(1).join("");
                              }
                              // Prevent leading zeros (except for decimal numbers like 0.5)
                              if (
                                value.length > 1 &&
                                value.startsWith("0") &&
                                !value.startsWith("0.")
                              ) {
                                value = value.replace(/^0+/, "");
                              }
                              // Prevent just "0" (but allow "0." for decimal input)
                              if (value === "0") {
                                value = "";
                              }
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              // Prevent negative sign
                              if (e.key === "-") {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent number input from changing value on scroll
                              e.currentTarget.blur();
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          The increment value for points
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address*</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter the complete address"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <VirtualizedCountrySelect
                        countries={(countries as any[]) || []}
                        value={field.value || ""}
                        onValueChange={(name) => {
                          if (name !== field.value) {
                            form.setValue("city", "", { shouldDirty: true });
                            form.clearErrors("city");
                          }
                          const selected = (countries as any[])?.find(
                            (c) => c.name === name,
                          );
                          setSelectedCountry(selected || null);
                          field.onChange(name);
                        }}
                        placeholder="Select Country"
                        label="Country*"
                        error={form.formState.errors.country?.message}
                        withForm
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <VirtualizedCitySelect
                        cities={selectedCountry?.cities || []}
                        value={field.value || ""}
                        onValueChange={(value) => {
                          form.setValue("city", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                        placeholder="Select City"
                        disabled={!selectedCountry?.cities?.length}
                        label="City*"
                        error={form.formState.errors.city?.message}
                        withForm={true}
                      />
                    )}
                  />
                </TabsContent>
                <TabsContent value="contacts" className="space-y-6">
                  <ContactForm form={form} />
                </TabsContent>
              </Tabs>
              <SheetFooter className="mt-6 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <LoaderButton type="submit" loading={isSubmitting}>
                  Save Changes
                </LoaderButton>
              </SheetFooter>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
